import { identify, logEvent } from "analytics";
import { getCurrencyCode, getOrg } from "utils/site";
import { SHA256, toPascalCase } from "utils/utils";
import { inProduction } from "../../../../config";
import { SpeciesEnum } from "../../../../__generated__/globalTypes";
import { selectMetadata } from "../store/selectors/metadata";
import {
  getCurrentDraftForAnimal,
  selectAllAnimals,
  selectCurrentAnimal,
} from "../store/selectors/animals";
import { StepName } from "../steps/types";
import { track } from "./track";
import { State } from "../store/state/types";
import { getMarketingProps } from "./marketingProps";
import { deviceId } from "../flow/utils/getDeviceId";
import {
  municipalityCodeToCounty,
  municipalityCodeToMunicipality,
} from "utils/locations";
import { AdtractionIdEnumSwe } from "utils/ad-network";
import { selectCustomer } from "../store/selectors/customer";
import { CheckMultiBind_checkMultiBind } from "../../../../__generated__/CheckMultiBind";
import {
  animalRoutes,
  sequentialAnimalRoutes,
} from "../store/middlewares/utils/updateRoute";
import { stepMap } from "../steps";

type EventNames = "Preventive Care Package selected";

export const trackSignupEvent = async (
  stepName: StepName | EventNames,
  stepIndex: number,
  state: State
): Promise<void> => {
  const stepNames = Object.keys(stepMap);

  const metadata = selectMetadata(state);
  const customer = selectCustomer(state);
  const animal = selectCurrentAnimal(state);

  let animals = null;

  if (stepNames.includes(stepName)) {
    animals = [...animalRoutes, ...sequentialAnimalRoutes].includes(
      stepName as StepName
    )
      ? [animal]
      : selectAllAnimals(state);
  } else {
    animals = [animal];
  }

  animals?.forEach((animal) =>
    animal.insurances.forEach(async (insurance) => {
      const signupEvent = {
        event: "InsuranceSignup",
        step: stepIndex + 1,
        stepName: toPascalCase(stepName),
        animalAccess: animal.access,
        animalBirthdate: animal.birthday || undefined,
        animalCastrated: animal.sexSituation
          ? animal.sexSituation === "castrated"
          : undefined,
        animalBreed: animal.breed?.id || undefined,
        animalSex: animal.sex || undefined,
        animalMixedBreedPrimary: animal.mixedBreedFHalf || undefined,
        animalMixedBreedSecondary: animal.mixedBreedSHalf || undefined,
        animalSpecies: animal.species || undefined,
        currency: getCurrencyCode(),
        discountCode: insurance.discountCode || undefined,
        deviceId: deviceId(),
        policyCopay: insurance.copay,
        policyDeductible: insurance.deductible / 100,
        policyNumber: insurance.policy?.policyNumber
          ? await SHA256(insurance.policy?.policyNumber)
          : undefined,
        policyLimit: insurance.limit / 100,
        product: insurance.product,
        healthHistory: animal.healthHistory,
        policyStartDate: insurance.startDate,
        value:
          ((getCurrentDraftForAnimal(animal)?.total ?? 0) / 100) * 7 * 0.17, // Estimated LTV
        userId: customer.clientId ? await SHA256(customer.clientId) : undefined,
        org: getOrg(),
        ...(getOrg() === "swe"
          ? {
              policyLifeLimit: (insurance.lifeLimit ?? 0) / 100,
              policyBreedersAddOn: insurance.breedersAddOn,
              policyLifeUtilityAddOn: insurance.lifeUtilityAddOn,
              compare: insurance.insurely?.shouldCompare ?? false,
              previousInsurance: insurance.previousInsurance,
            }
          : {}),
        fromApp: metadata.fromApp,
        userEmail: customer.email ? await SHA256(customer.email) : undefined,
      };

      if (customer.clientId) {
        await identify(undefined, {
          resparkClientId: await SHA256(customer.clientId),
        });
      }

      if (!inProduction) {
        console.log("TRACK", signupEvent);
      }

      track(signupEvent);

      await logEvent(
        `INSURANCE_SIGNUP_STEP_${stepName.toUpperCase()}`,
        signupEvent
      );
    })
  );
};

export const trackPurchase = async (
  policy: CheckMultiBind_checkMultiBind["policy"],
  paymentMethod: string,
  successful: boolean,
  fromApp?: boolean
): Promise<void> => {
  if (!policy) return;

  const isMixedBreed = policy.ratingsData?.breed?.includes(",");

  const species = /^(OUAF|WUFF|VOFF)/.test(policy.policyNumber)
    ? SpeciesEnum.dog
    : SpeciesEnum.cat;

  const purchaseEvent = {
    event: successful ? "purchase" : "paymentFailed",
    animalBirthdate: policy.ratingsData?.birthday ?? "",
    animalCastrated: policy.ratingsData?.sex?.includes("castrated") ?? false,
    animalBreed: policy.ratingsData?.breed ?? "",
    animalSex: policy.ratingsData?.sex?.replace("-castrated", "") ?? "",
    animalMixedBreedPrimary:
      (isMixedBreed && policy.ratingsData?.breed?.split(",")[0]) || undefined,
    animalMixedBreedSecondary:
      (isMixedBreed && policy.ratingsData?.breed?.split(",")[1]) || undefined,
    animalSpecies: species,
    animalAccess: policy.ratingsData?.access ?? "",
    currency: getCurrencyCode(),
    discountCode: policy.metadata?.discount || undefined,
    paymentMethod,
    policyCopay: policy.ratingsData?.copay ?? 0.15,
    policyDeductible: policy.deductible / 100,
    policyNumber: await SHA256(policy.policyNumber),
    policyLimit: policy.limit / 100,
    policyStartDate: policy.effectiveDate,
    policyBillingInterval: policy.billingInterval,
    policyBillingDay: policy.billingDay,
    value: ((policy?.total ?? 0) / 100) * 7 * 0.17 || undefined, // Estimated LTV
    userId: policy?.client?._id ? await SHA256(policy?.client?._id) : undefined,
    org: getOrg(),

    ...(getOrg() === "swe"
      ? {
          animalMunicipality: municipalityCodeToMunicipality(
            policy.client?.metadata?.municipality
          ),
          animalCounty: municipalityCodeToCounty(
            policy.client?.metadata?.municipality
          ),
          policyLifeLimit: (policy.ratingsData?.lifeLimit ?? 0) / 100,
          policyBreedersAddOn: policy.ratingsData?.breedersAddOn,
          policyLifeUtilityAddOn: policy.ratingsData?.lifeUtilityAddOn,
          compare: !!policy.metadata?.previousInsurance,
          cancelPreviousInsurance: policy.metadata?.cancelPreviousInsurance,
          adtractionId:
            species === SpeciesEnum.cat
              ? AdtractionIdEnumSwe.Cat
              : AdtractionIdEnumSwe.Dog,
        }
      : {}),

    fromApp,
    ...getMarketingProps(policy, species),
    userEmail: policy?.client?.email
      ? await SHA256(policy?.client?.email)
      : undefined,
  };
  if (!inProduction) {
    console.log("TRACK", purchaseEvent);
  }
  if (policy?.client?._id) {
    await identify(undefined, {
      resparkClientId: await SHA256(policy?.client?._id),
    });
  }
  track(purchaseEvent);
  await logEvent(successful ? "PURCHASE" : "PAYMENT_FAILED", purchaseEvent);
};
